import React from 'react';
import ReactDOM from 'react-dom';
import { Loader2 } from 'lucide-react';
import './PageLoadOverlay.css';

export interface PageLoadOverlayProps {
    /** When false the overlay renders nothing. */
    visible: boolean;
    /** Whole seconds since the load began. */
    elapsedSeconds: number;
}

const DOUBLE_PRIME = '″';
const PRIME = '′';

/**
 * Format elapsed seconds for the overlay counter:
 *   - Sub-60 s : zero-padded ``01″`` ... ``59″``.
 *   - >= 60 s  : ``mm′ss″`` (e.g. ``02′05″`` for 125 s).
 *
 * Negative or fractional inputs are clamped to floor(max(0, n)) so the
 * counter never displays garbage during the brief window between
 * ``setState`` and the next interval tick.
 *
 * Exported for unit testing.
 */
export function formatElapsed(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds));
    if (safe < 60) {
        return `${String(safe).padStart(2, '0')}${DOUBLE_PRIME}`;
    }
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    return `${String(m).padStart(2, '0')}${PRIME}${String(s).padStart(2, '0')}${DOUBLE_PRIME}`;
}

/**
 * Page-load overlay for /farm and /documents primary-load states
 * (issue #643).
 *
 * Rendered into ``document.body`` via ``createPortal`` so any parent
 * ``overflow: hidden`` / ``transform`` stacking context cannot clip it.
 * The semi-transparent backdrop swallows pointer events so double-clicks
 * during a slow load do not fire twice once the load resolves.
 *
 * Pure presentation - the surrounding ``usePageLoadTelemetry`` hook
 * owns the timer + flicker-threshold gating and tells this component
 * when to be visible.
 */
export const PageLoadOverlay: React.FC<PageLoadOverlayProps> = ({ visible, elapsedSeconds }) => {
    if (!visible || typeof document === 'undefined') {
        return null;
    }

    const showCounter = Math.floor(Math.max(0, elapsedSeconds)) >= 1;
    const node = (
        <div
            className="page-load-overlay__backdrop"
            role="status"
            aria-live="polite"
            aria-label="Daten werden geladen"
        >
            <div className="page-load-overlay__panel">
                <div className="page-load-overlay__spinner-wrap">
                    <Loader2
                        className="page-load-overlay__spinner"
                        size={64}
                        aria-hidden="true"
                    />
                    {showCounter && (
                        <span className="page-load-overlay__counter">{formatElapsed(elapsedSeconds)}</span>
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(node, document.body);
};

export default PageLoadOverlay;
